import {
    all,
    call,
    cancel,
    fork,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import axios from 'axios';
import keyBy from 'lodash.keyby';
import get from 'lodash.get';
import { stopSubmit } from 'redux-form';

// Constants
import {
    TIMESHEET_ENTRIES_LOAD_REQUESTED,
    TIMESHEET_ENTRIES_LOAD_CANCELED,
    TIMESHEET_ENTRY_UPDATE_REQUESTED,
    TIMESHEET_ENTRIES_APPROVE_REQUESTED,
    TIMESHEET_ENTRIES_DELETE_REQUESTED
} from './constants';

// Actions
// @ts-ignore
import { enqueueSnackbar } from '../notifications/actions';
// @ts-ignore
import { setResult } from '../results/actions';
// @ts-ignore
import { setCount } from '../counts/actions';
import {
    succeededLoadTimesheetEntries,
    failedLoadTimesheetEntries,
    succeededUpdateTimesheetEntry,
    failedUpdateTimesheetEntry,
    succeededApproveTimesheetEntries,
    failedApproveTimesheetEntries,
    succeededDeleteTimesheetEntries,
    failedDeleteTimesheetEntries,
    setTimesheetEntries
} from './actions';
import { updateQueryString } from '../router/actions';

// Selectors
// @ts-ignore
import { idTokenSelector } from '../auth/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import { selectLoadTimesheetEntriesParams } from './selectors';

// Utils
import axiosPoll from '../../utils/axiosPoll';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadTimesheetEntriesSucceeded(response: any): any {
    const { data } = response;
    const count = get(response, 'headers.x-total-count');

    const timesheetEntriesKeyedByUuid = keyBy(data, 'uuid');
    const timesheetEntryUuids = data.map((value: any) => value.uuid);

    // Set Timesheet Entries
    yield put(setTimesheetEntries(timesheetEntriesKeyedByUuid));

    // Set Count
    yield put(setCount('TIMESHEET_ENTRIES_LOAD', count));

    // Set Result
    yield put(setResult('TIMESHEET_ENTRIES_LOAD', timesheetEntryUuids));

    yield put(succeededLoadTimesheetEntries(response));
}

export function* loadTimesheetEntriesFailed(error: any): any {
    yield put(failedLoadTimesheetEntries(error));
}

export function* loadTimesheetEntries(): any {
    // Select params
    const params = yield select(selectLoadTimesheetEntriesParams);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries`,
            params
        },
        succeeded: loadTimesheetEntriesSucceeded,
        failed: loadTimesheetEntriesFailed
    });
}

export function* watchTimesheetEntriesLoadRequested(): any {
    let lastTask;
    while (true) {
        const [requested] = yield race([
            take(TIMESHEET_ENTRIES_LOAD_REQUESTED),
            take(TIMESHEET_ENTRIES_LOAD_CANCELED)
        ]);
        if (lastTask) {
            yield cancel(lastTask);
        }
        if (requested) {
            lastTask = yield fork(loadTimesheetEntries);
        }
    }
}

export function* updateTimesheetEntry(action: any): any {
    const {
        payload: { timesheetEntryUuid, data }
    } = action;

    const idToken = yield select(idTokenSelector);

    try {
        // @ts-ignore
        const putResponse = yield call(axios, {
            method: 'put',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries/bulk`,
            headers: {
                Authorization: idToken
            },
            data: {
                update: [
                    {
                        uuid: timesheetEntryUuid,
                        ...data
                    }
                ]
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetEntriesParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetEntriesSucceeded, getResponse);

        // Succeeded Update Timesheet Entry
        yield put(succeededUpdateTimesheetEntry(putResponse));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet entry updated successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                timesheetEntryModal: undefined,
                selectedTimesheetEntry: undefined,
                selectedWorkType: undefined,
                defaultValues: undefined
            })
        );
    } catch (error) {
        yield put(failedUpdateTimesheetEntry(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('TIMESHEET_ENTRY', { _error: message }));
    }
}

export function* watchUpdateTimesheetEntries(): any {
    yield takeLatest(TIMESHEET_ENTRY_UPDATE_REQUESTED, updateTimesheetEntry);
}

export function* approveTimesheetEntries(action: any): any {
    const {
        payload: { timesheetEntryUuids }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    // Select Current Organization Id
    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/TimesheetReviews`;

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'post',
            url,
            headers: {
                Authorization: idToken
            },
            data: {
                organizationId,
                isApproved: true,
                timesheetEntryUuids
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetEntriesParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetEntriesSucceeded, getResponse);

        // Succeeded Approve Timesheet Entry
        yield put(succeededApproveTimesheetEntries(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet entries approved successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                approveTimesheetEntriesModal: undefined
            })
        );
    } catch (error) {
        yield put(failedApproveTimesheetEntries(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('TIMESHEET_ENTRIES_APPROVE', { _error: message }));
    }
}

export function* watchTimesheetApproveLoadRequested(): any {
    yield takeLatest(
        TIMESHEET_ENTRIES_APPROVE_REQUESTED,
        approveTimesheetEntries
    );
}

export function* deleteTimesheetEntries(action: any): any {
    const {
        payload: { timesheetEntryUuids }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    try {
        // @ts-ignore
        const putResponse = yield call(axios, {
            method: 'put',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries/bulk`,
            headers: {
                Authorization: idToken
            },
            data: {
                delete: timesheetEntryUuids
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetEntriesParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/TimesheetEntries`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetEntriesSucceeded, getResponse);

        // Succeeded Delete Timesheet Entry
        yield put(succeededDeleteTimesheetEntries(putResponse));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet entries deleted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                deleteTimesheetEntriesModal: undefined
            })
        );
    } catch (error) {
        yield put(failedDeleteTimesheetEntries(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('TIMESHEET_ENTRIES_DELETE', { _error: message }));
    }
}

export function* watchTimesheetDeleteLoadRequested(): any {
    yield takeLatest(
        TIMESHEET_ENTRIES_DELETE_REQUESTED,
        deleteTimesheetEntries
    );
}

export default function* timesheetEntriesSagas(): any {
    yield all([
        fork(watchTimesheetEntriesLoadRequested),
        fork(watchUpdateTimesheetEntries),
        fork(watchTimesheetApproveLoadRequested),
        fork(watchTimesheetDeleteLoadRequested)
    ]);
}
